// Tout ce que FlashCP DÉDUIT du blob `rapport` de la V2 : les compteurs des
// étapes, l'indicateur « prêt à valider », le reste d'hier.
//
// Deux règles tenues partout ici :
//   • on ne demande jamais ce qui est déductible (P5) ;
//   • la machine constate, elle ne déclenche rien (P4) — ces fonctions
//     n'écrivent pas, elles allument une lampe.
//
// Les conventions V2 sont respectées à la lettre :
//   • statut de ticket VALIDÉ = `null` dans <kind>Statuses, clé ABSENTE = jamais
//     vu, donc « manquant » — un ticket jamais vu ne compte pas comme traité ;
//   • un paiement est PAD si reglementId === 62 ou si le libellé contient P.A.D
//     (isPadPayment, source unique du parseur partagé) ;
//   • l'inventaire du jour vit dans rapport.inventaireCoffre, pas dans la table
//     inventaire_coffre (abandonnée en avril 2026).

import { isPadPayment } from 'sotcp-core/csvParser'
import { sumApport } from 'sotcp-core/inventaireCoffreCalc'
import { lirePrelevSco, totauxCoffre } from './especes'
import type { Rapport, PadVerif } from './types'

export type Kind = 'retours' | 'formation' | 'annules'

const tableau = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? (v as Record<string, unknown>[]) : []

const objet = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}

// ── 01 Import ──────────────────────────────────────────────────────────────

export interface FichierImport {
  id: 'reglements' | 'rapport_operateur' | 'retours' | 'operations'
  nom: string
  charge: boolean
  detail: string
}

export function etatImport(r: Rapport | null): { fichiers: FichierImport[]; charges: number } {
  const rap = r ?? {}
  const payments = tableau(rap.payments)
  const ops      = tableau(rap.rapportOps)
  const retours  = tableau(rap.retours)
  const opsCharg = rap.operationsLoaded === true

  const fichiers: FichierImport[] = [
    {
      id: 'reglements', nom: 'Suivi des règlements',
      charge: Array.isArray(rap.payments),
      detail: Array.isArray(rap.payments) ? `${payments.length} règlements` : 'non chargé',
    },
    {
      // Un export d'opérateurs légitimement vide reste un fichier CHARGÉ.
      // Le juger sur sa longueur faisait dire « 3 / 4 fichiers » à la lampe de
      // l'étape pendant que l'écran affichait « 4 / 4 déposés » — deux vérités
      // contradictoires à dix centimètres l'une de l'autre.
      id: 'rapport_operateur', nom: 'Rapport opérateur',
      charge: Array.isArray(rap.rapportOps),
      detail: Array.isArray(rap.rapportOps)
        ? (ops.length > 0 ? `${ops.length} opérateurs` : 'chargé, aucune ligne')
        : 'non chargé',
    },
    {
      id: 'retours', nom: 'États des retours',
      charge: Array.isArray(rap.retours),
      detail: Array.isArray(rap.retours) ? `${retours.length} tickets` : 'non chargé',
    },
    {
      id: 'operations', nom: 'Opérations à risque',
      charge: opsCharg,
      detail: opsCharg
        ? `${tableau(rap.annules).length} annulés · ${tableau(rap.formation).length} formation`
        : 'non chargé',
    },
  ]
  return { fichiers, charges: fichiers.filter((f) => f.charge).length }
}

// ── 02 Apports ─────────────────────────────────────────────────────────────

/**
 * Une question d'entrée devient sans objet dès que la réponse est déductible.
 *
 * Demander « des apports ont-ils été faits aujourd'hui ? » alors que l'étape 02A
 * vient d'en inscrire, c'est demander ce qu'on sait (P5) — et, pire, un « non »
 * écarterait l'étape avec les apports SCO dedans : ils deviendraient invisibles
 * et le papier d'apport impossible à imprimer.
 */
export function questionSansObjet(code: string, r: Rapport | null): boolean {
  if (code !== '02') return false
  return Object.keys(objet(r?.apportsSco)).length > 0
}

export interface LigneApport { caissier: string; plomb: string | null; total: number }

export function etatApports(r: Rapport | null): { lignes: LigneApport[]; total: number } {
  const src = objet(r?.apports)
  const lignes = Object.entries(src).map(([caissier, v]) => {
    const d = objet(v)
    return {
      caissier,
      plomb: typeof d.plomb === 'string' ? d.plomb : null,
      total: sumApport(d as { rouleaux?: Record<string, unknown>; billets?: Record<string, unknown> }),
    }
  })
  return { lignes, total: lignes.reduce((s, l) => s + l.total, 0) }
}

/**
 * Ce qui a été rechargé dans les monnayeurs — étape 02A.
 *
 * Le montant est celui qui a été SAISI (`valeur`), y compris quand l'humain a
 * corrigé la proposition de l'algorithme : c'est ce qui est physiquement sorti
 * du coffre qui compte, pas ce qu'on avait calculé.
 */
export function sommeApportsSco(r: Rapport | null): number {
  return Object.values(objet(r?.apportsSco))
    .reduce<number>((s, v) => s + (Number(objet(v).valeur) || 0), 0)
}

/**
 * TOUT CE QUI EST SORTI DU COFFRE EN MONNAIE : les caissiers ET les machines.
 *
 * Le calcul du coffre ne retranchait que les apports aux caissiers. Une
 * recharge de monnayeur est pourtant le même mouvement physique — de la monnaie
 * qu'on sort du coffre pour la charger dans une machine — et l'oublier laissait
 * le solde attendu trop haut, donc un manque fantôme dans l'écart : 183 € sur
 * la seule journée du 18/08.
 *
 * Cette fonction existe pour que les DEUX endroits qui alimentent le calcul
 * (l'écran du coffre et `etatCoffre`) posent forcément la même question.
 */
export function apportsSortis(r: Rapport | null): number {
  return etatApports(r).total + sommeApportsSco(r)
}

// ── 03 PAD ─────────────────────────────────────────────────────────────────

export interface LignePad {
  id: string
  ref: string
  montant: number
  caisse: string
  ticket: string
  verif: PadVerif | null
  /** Statué = une issue a été choisie parmi les quatre (§7.3). */
  statue: boolean
  /** Écart entre le montant ORIKA et le montant réellement reçu. */
  ecart: number
}

/**
 * Le montant réel fait foi : c'est lui qui part dans les totaux cartes et
 * chèques de l'étape 11, puisque c'est l'argent réellement encaissé. La
 * différence part expliquer l'écart de flux (§7.3).
 */
export function etatPad(r: Rapport | null): {
  lignes: LignePad[]; statues: number; ecartTotal: number; total: number
} {
  const verifs = objet(r?.padVerifs) as Record<string, PadVerif | undefined>
  const lignes: LignePad[] = tableau(r?.payments)
    .filter((p) => isPadPayment(p as { reglementId?: number | null; typeReglement?: string | null }))
    .map((p) => {
      const id = String(p.id ?? '')
      const montant = Number(p.montant) || 0
      const v = verifs[id] ?? null
      const reel = typeof v?.montant_reel === 'number' ? v.montant_reel : null
      return {
        id,
        ref: refPad(p),
        montant,
        caisse: String(p.caisse ?? '—'),
        ticket: String(p.ticket ?? '—'),
        verif: v,
        statue: estPadStatue(v),
        ecart: reel === null ? 0 : +(reel - montant).toFixed(2),
      }
    })
  return {
    lignes,
    statues: lignes.filter((l) => l.statue).length,
    ecartTotal: +lignes.reduce((s, l) => s + l.ecart, 0).toFixed(2),
    total: +lignes.reduce((s, l) => s + l.montant, 0).toFixed(2),
  }
}

/**
 * La référence lisible d'un PAD.
 *
 * ORIKA n'en fournit pas : la colonne référence des lignes P.A.D contient la
 * chaîne littérale « None » (constaté sur les exports d'août 2026). Afficher
 * « None » à l'écran serait absurde, et s'en servir comme clé de la file
 * d'attente rendrait deux PAD du même jour indiscernables. On retombe donc sur
 * le couple caisse / ticket, qui identifie la ligne de façon stable.
 */
export function refPad(p: Record<string, unknown>): string {
  const brute = String(p.reference ?? '').trim()
  if (brute && !/^(none|null|-|—|n\/?a)$/i.test(brute)) return brute
  return `PAD ${String(p.caisse ?? '?')}/${String(p.ticket ?? '?')}`
}

/**
 * Quatre issues, deux natures (§7.3) :
 *   justificatif présent → carte télécollectée ? / chèque au bon montant ?
 *   justificatif absent  → carte télécollectée ? oui = clos, non = PAD en attente.
 * Dans tous les cas, une issue a été choisie : l'étape peut être validée.
 */
export function estPadStatue(v: PadVerif | null | undefined): boolean {
  if (!v || !v.type) return false
  if (v.recu === true) {
    return v.type === 'carte' ? v.telecollecte !== null : v.conforme !== null
  }
  if (v.recu === false) return v.telecollecte !== null
  return false
}

// ── 05a Dépouillement ──────────────────────────────────────────────────────

export function etatDepouillement(r: Rapport | null): { total: number; verifies: number } {
  const verifs = objet(r?.verifications)
  const nonPad = tableau(r?.payments).filter(
    (p) => !isPadPayment(p as { reglementId?: number | null; typeReglement?: string | null }),
  )
  const verifies = nonPad.filter((p) =>
    Object.prototype.hasOwnProperty.call(verifs, String(p.id ?? '')),
  ).length
  return { total: nonPad.length, verifies }
}

// ── 06 / 07 / 08 Tickets ───────────────────────────────────────────────────

export interface EtatTickets {
  total: number
  traites: number
  valides: number
  anomalies: number
  manquants: number
  jamaisVus: number
}

export function etatTickets(r: Rapport | null, kind: Kind): EtatTickets {
  const lignes = tableau(r?.[kind])
  const statuses = objet(r?.[`${kind}Statuses`])
  const e: EtatTickets = { total: lignes.length, traites: 0, valides: 0, anomalies: 0, manquants: 0, jamaisVus: 0 }

  const vus = new Set<string>()
  for (const l of lignes) {
    const ticket = String(l.ticket ?? '')
    if (vus.has(ticket)) continue          // même ticket, plusieurs lignes
    vus.add(ticket)
    if (!Object.prototype.hasOwnProperty.call(statuses, ticket)) { e.jamaisVus++; continue }
    e.traites++
    const st = statuses[ticket]
    if (st === null) { e.valides++; continue }
    const code = String(objet(st).status ?? '')
    if (code === 'valide') e.valides++
    else if (code === 'manquant') e.manquants++
    else e.anomalies++
  }
  e.total = vus.size
  return e
}

/**
 * Tickets de formation statués mais sans motif — la seule information que cette
 * étape produit vraiment. Un statut V2 vaut `null` (validé sans détail) ou un
 * objet ; seul l'objet peut porter un motif, donc `null` compte comme sans motif.
 */
export function ticketsSansMotif(r: Rapport | null): number {
  const statuses = objet(r?.formationStatuses)
  const lignes = tableau(r?.formation)
  const vus = new Set<string>()
  let sans = 0
  for (const l of lignes) {
    const ticket = String(l.ticket ?? '')
    if (vus.has(ticket)) continue
    vus.add(ticket)
    if (!Object.prototype.hasOwnProperty.call(statuses, ticket)) continue
    const st = statuses[ticket]
    const motif = st === null ? '' : String(objet(st).motif ?? '')
    if (!motif.trim()) sans++
  }
  return sans
}

// ── 09 Opérations à risque ─────────────────────────────────────────────────

export function etatOperations(r: Rapport | null): { lignes: number; familles: Array<{ nom: string; n: number }> } {
  const familles = [
    { nom: 'Annulations de ligne', n: tableau(r?.annulationsLigne).length },
    { nom: 'Remises',              n: tableau(r?.remises).length },
    { nom: 'Changements de prix',  n: tableau(r?.changementsPrix).length },
    { nom: 'Autorisations superviseur', n: tableau(r?.autorisations).length },
  ]
  return { lignes: familles.reduce((s, f) => s + f.n, 0), familles }
}

// ── 10 Coffre ──────────────────────────────────────────────────────────────

export interface EtatCoffre {
  existe: boolean
  totalCompte: number
  soldeAttendu: number
  ecart: number
  motifEcart: string
  prelevementsJTotal: number
}

export function etatCoffre(r: Rapport | null): EtatCoffre {
  const inv = objet(r?.inventaireCoffre)
  const existe = Object.keys(inv).length > 0

  // Sans inventaire, il n'y a pas d'écart : le calcul rendrait ici
  // 0 − (0 − apports) = le total des apports, un « écart » qui n'existe pas et
  // qui se retrouverait tel quel dans le bilan et dans la saisie COL MNC.
  if (!existe) {
    return { existe: false, totalCompte: 0, soldeAttendu: 0, ecart: 0, motifEcart: '', prelevementsJTotal: 0 }
  }

  // Les enveloppes scellées du vidage SCO comptent dans le coffre sans y avoir
  // été comptées : `totauxCoffre` les ajoute, comme le fait le serveur. Et les
  // apports retranchés incluent les recharges de monnayeurs — même mouvement
  // que les apports aux caissiers.
  const t = totauxCoffre({
    inv, apportsTotal: apportsSortis(r), prelevSco: lirePrelevSco(r),
  })
  return {
    existe,
    totalCompte: t.total_compte,
    soldeAttendu: t.solde_attendu,
    ecart: t.ecart,
    motifEcart: typeof inv.commentaire_ecart === 'string' ? inv.commentaire_ecart : '',
    prelevementsJTotal: t.prelevementsJTotal,
  }
}

// ── 04 Prélèvements ────────────────────────────────────────────────────────

/** Une enveloppe de prélèvement vaut 500 € — la convention du poste. */
export const VALEUR_ENVELOPPE = 500

export interface EtatPrelevements {
  /** ATTENTION : des NOMBRES D'ENVELOPPES, pas des montants. */
  coffre: number
  papier: number
  /** Écart en enveloppes. Un écart de 1 se lit « il manque une enveloppe ». */
  ecart: number
  /** Enveloppes annoncées par le rapport opérateur (prelevCount). */
  attendu: number
  /** Le même écart en euros, pour le bilan et les totaux. */
  ecartEuros: number
  compte: boolean
}

/**
 * `coffrePrelev` et `paperPrelev` sont des COMPTAGES, pas des montants : le
 * parseur pose `prelevCount = round(prelev / 500)`. Le contrôle du jour est un
 * comptage d'enveloppes — les afficher en euros, comme le faisait cette
 * fonction, transformait « il manque 9 enveloppes » en « écart de 9,00 € ».
 */
export function etatPrelevements(r: Rapport | null): EtatPrelevements {
  const coffre = Math.max(0, Math.trunc(Number(r?.coffrePrelev) || 0))
  const papier = Math.max(0, Math.trunc(Number(r?.paperPrelev) || 0))
  const attendu = tableau(r?.rapportOps)
    .reduce((s, o) => s + Math.max(0, Math.trunc(Number(o.prelevCount) || 0)), 0)

  // LA CONCORDANCE EST À TROIS TERMES, PAS À DEUX.
  //
  // La règle comparait `coffre` à `papier`, et elle était fausse depuis
  // toujours : le papier porte TOUTES les enveloppes de la journée — celles
  // faites par les caissiers ET celles faites au coffre. Le coffre seul n'en
  // couvre qu'une part, donc l'écart n'était jamais nul et l'étape ne pouvait
  // se valider qu'en forçant.
  //
  // Vérifié sur les quatre runs existantes, sans exception :
  //
  //     12/08   coffre 2 + caissiers 6  = papier 8    ✓   (ancienne règle : 2 ≠ 8)
  //     11/08   coffre 0 + caissiers 9  = papier 9    ✓   (ancienne règle : 0 ≠ 9)
  //     10/08   coffre 6 + caissiers 9  = papier 15   ✓   (ancienne règle : 6 ≠ 15)
  //     09/08   coffre 0 + caissiers 3  = papier 3    ✓   (ancienne règle : 0 ≠ 3)
  //
  // Quatre journées sur quatre pour la nouvelle règle, zéro sur quatre pour
  // l'ancienne : ce n'était pas un cas limite, c'était le cas nominal.
  const ecart = coffre + attendu - papier
  return {
    coffre, papier, ecart, attendu,
    ecartEuros: ecart * VALEUR_ENVELOPPE,
    // Le rapport opérateur annonce des enveloppes : le contrôle a de la matière
    // même si personne n'a encore saisi de comptage.
    compte: coffre > 0 || papier > 0,
  }
}

// ── Les règles de grisage, côté client ─────────────────────────────────────

/**
 * MIROIR EXACT de public.fc_regles(). Le serveur reste la référence : c'est lui
 * qui écrit skip_regle en base. Cette copie ne sert qu'à annoncer à l'accueil,
 * AVANT d'ouvrir la run, ce qui sera grisé d'office — les deux doivent donc
 * dire la même chose. Un test unitaire et le test SQL couvrent les mêmes cas.
 *
 * Garde essentielle, identique des deux côtés : une étape n'est grisée que si
 * le fichier correspondant a été importé. Une journée vierge n'a aucune étape
 * grisée — l'absence d'import n'est pas une absence de travail.
 */
export function regles(r: Rapport | null): Record<string, string> {
  const res: Record<string, string> = {}
  if (!r) return res

  const reglementsCharges = Array.isArray(r.payments)
  const retoursCharges    = Array.isArray(r.retours)
  const opsChargees       = r.operationsLoaded === true
                         || Array.isArray(r.annules) || Array.isArray(r.formation)

  if (reglementsCharges) {
    const pads = tableau(r.payments).filter(
      (p) => isPadPayment(p as { reglementId?: number | null; typeReglement?: string | null }),
    )
    if (pads.length === 0) res['03'] = 'Aucun PAD dans l’import ORIKA du jour'
  }
  if (retoursCharges && tableau(r.retours).length === 0) {
    res['07'] = 'Aucune ligne de retour dans l’import ORIKA du jour'
  }
  if (opsChargees) {
    if (tableau(r.formation).length === 0) {
      res['08'] = 'Aucune ligne « mode formation » dans l’import ORIKA du jour'
    }
    if (tableau(r.annules).length === 0) {
      res['09'] = 'Aucun ticket annulé dans l’import ORIKA du jour'
    }
  }
  return res
}

// ── L'indicateur « prêt à valider » (§4.4) ─────────────────────────────────

export interface Pret {
  ok: boolean
  texte: string
}

/**
 * Chaque étape expose sa condition de complétude, évaluée en continu.
 * Le bouton de validation reste TOUJOURS actif : tu peux valider malgré tout,
 * une confirmation apparaît, et la validation forcée est tracée au journal.
 */
/**
 * @param etape  Facultatif, mais nécessaire aux étapes dont la complétude ne se
 *   lit pas dans le blob : l'étape 11 coche ses documents dans `avancement`.
 *   Sans lui, sa lampe restait ambre quoi qu'on fasse et toute validation
 *   passait par le forçage — une étape qu'on ne peut jamais finir proprement.
 */
export function pret(code: string, r: Rapport | null, etape?: { avancement?: Record<string, unknown> }): Pret {
  switch (code) {
    case '01': {
      const { charges, fichiers } = etatImport(r)
      return { ok: charges === 4, texte: `${charges} / ${fichiers.length} fichiers chargés` }
    }
    case '02': {
      const { lignes } = etatApports(r)
      return lignes.length > 0
        ? { ok: true,  texte: `${lignes.length} caissier${lignes.length > 1 ? 's' : ''} saisi${lignes.length > 1 ? 's' : ''}` }
        : { ok: false, texte: 'aucun apport saisi' }
    }
    case '02A': {
      // La complétude de cette étape ne vit pas dans le blob : elle dépend des
      // tickets déposés dans fc_imports. L'écran la dépose dans l'avancement.
      //
      // Ce qui rend l'étape « prête », c'est d'avoir rechargé ce qu'il fallait —
      // PAS d'avoir lu les quatre tickets. Une machine sans ticket est un
      // manque signalé, pas une faute : l'exiger rendrait la lampe verte
      // impossible les jours où une caisse n'imprime rien, et une lampe qui ne
      // peut jamais s'allumer ne dit plus rien.
      const sco = (etape?.avancement?.sco ?? null) as Record<string, number> | null
      if (!sco) return { ok: false, texte: 'vérifications non lues' }
      const att = Number(sco.attendues) || 0
      const exp = Number(sco.exploitables) || 0
      const aFaire = Number(sco.aFaire) || 0
      const faites = Number(sco.faites) || 0
      if (att === 0) return { ok: false, texte: 'aucune machine configurée' }
      if (exp === 0) return { ok: false, texte: 'aucun ticket exploitable' }
      const reste = att - exp
      const suffixe = reste > 0 ? ` · ${reste} non lue${reste > 1 ? 's' : ''}` : ''
      if (aFaire === 0) return { ok: true, texte: `rien à recharger${suffixe}` }
      return {
        ok: faites >= aFaire,
        texte: `${faites} / ${aFaire} machine${aFaire > 1 ? 's' : ''} inscrite${aFaire > 1 ? 's' : ''} aux apports${suffixe}`,
      }
    }
    case '03': {
      const { statues, lignes } = etatPad(r)
      return { ok: statues === lignes.length, texte: `${statues} / ${lignes.length} PAD statués` }
    }
    case '04': {
      const { ecart, compte, attendu, coffre, papier } = etatPrelevements(r)
      // Ne rien avoir compté n'est pas une concordance : c'est un contrôle qui
      // n'a pas eu lieu. Sans cette garde, une étape jamais ouverte s'annonçait
      // « prête à valider ».
      if (!compte) {
        return attendu > 0
          ? { ok: false, texte: `${attendu} enveloppes annoncées, aucune comptée` }
          : { ok: false, texte: 'comptage non commencé' }
      }
      if (ecart === 0) {
        return { ok: true, texte: 'caissiers + coffre = papier' }
      }
      const n = Math.abs(ecart)
      // On nomme les trois termes : « 6 d'écart » sans dire entre quoi et quoi
      // oblige à rouvrir l'étape pour comprendre lequel est faux.
      return {
        ok: false,
        texte: `${attendu} caissiers + ${coffre} coffre ≠ ${papier} papier`
             + ` · ${n} enveloppe${n > 1 ? 's' : ''} d’écart`,
      }
    }
    case '05A':
      // Rapport opérateurs : une revue. La validation vaut accusé de lecture.
      return { ok: true, texte: 'revue — la validation vaut accusé de lecture' }
    case '05B': {
      const { total, verifies } = etatDepouillement(r)
      // Zéro règlement non-PAD n'est pas un contrôle inachevé : c'est un
      // contrôle sans objet. La lampe restait ambre pour toujours.
      if (total === 0) {
        return Array.isArray(r?.payments)
          ? { ok: true,  texte: 'aucun règlement à vérifier' }
          : { ok: false, texte: 'suivi des règlements non importé' }
      }
      return { ok: verifies === total, texte: `${verifies} / ${total} règlements vérifiés` }
    }
    case '07': case '09': {
      const kind: Kind = code === '07' ? 'retours' : 'annules'
      const e = etatTickets(r, kind)
      if (e.total === 0) return { ok: false, texte: 'aucun ticket importé' }
      return {
        ok: e.jamaisVus === 0,
        texte: e.jamaisVus > 0
          ? `${e.jamaisVus} ticket${e.jamaisVus > 1 ? 's' : ''} jamais vu${e.jamaisVus > 1 ? 's' : ''}`
          : `${e.traites} / ${e.total} tickets statués`,
      }
    }
    case '08': {
      // Formation : le motif libre est obligatoire pour valider un ticket
      // (§7.7, point ouvert O9). Un ticket validé sans motif laisserait une
      // information inutile — la lampe ne passe donc pas au vert.
      const e = etatTickets(r, 'formation')
      const sans = ticketsSansMotif(r)
      if (e.total === 0) return { ok: false, texte: 'aucun ticket importé' }
      if (e.jamaisVus === 0 && sans === 0) {
        return { ok: true, texte: `${e.traites} / ${e.total} tickets statués` }
      }
      return {
        ok: false,
        texte: sans > 0
          ? `${sans} ticket${sans > 1 ? 's' : ''} sans motif`
          : `${e.traites} / ${e.total} tickets statués`,
      }
    }
    case '10': {
      const c = etatCoffre(r)
      if (!c.existe) return { ok: false, texte: 'inventaire non commencé' }
      if (Math.abs(c.ecart) >= 0.01 && !c.motifEcart.trim()) {
        return { ok: false, texte: `écart de ${c.ecart.toFixed(2)} € sans motif` }
      }
      return { ok: true, texte: 'inventaire complet' }
    }
    case '11': {
      const docs = (etape?.avancement?.docs ?? null) as Record<string, boolean> | null
      if (!docs) return { ok: false, texte: 'documents à cocher au fur et à mesure' }
      const coches = Object.values(docs).filter(Boolean).length
      return coches > 0
        ? { ok: true, texte: `${coches} document${coches > 1 ? 's' : ''} imprimé${coches > 1 ? 's' : ''}` }
        : { ok: false, texte: 'aucun document imprimé' }
    }
    default:
      return { ok: true, texte: 'aucun contrôle bloquant' }
  }
}
